import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectLean } from 'lean-redux';
import { withRouter } from 'react-router';
import { get, reduce } from 'lodash';
import { withCourseOutline } from 'chameleon-packages/units';
import Sidebar from 'chameleon/ui-stack/ui/basic/side-bar-two-levels';
import FirstLevelNavList from 'chameleon/ui-stack/ui/base/first-level-nav-list';
import NavItemsContainer from 'chameleon/ui-stack/ui/sidebar-two-levels/nav-items-container';
import useNavigation, {
  PAGE_ACTIONS,
} from 'chameleon/ui-stack/hooks/useNavigation';
import useLastAccessedUnit from 'chameleon/ui-stack/hooks/use-last-accessed-unit';
import FirstLevelItem from 'chameleon/ui-stack/ui/sidebar-two-levels/first-level-item';
import SidebarLabelDisplay from 'chameleon/legacy/src/components/TeacherOrganize/components/SidebarLabelDisplay';
import {
  getAssignmentStatuses,
  REPORTS_LABEL,
  PROGRESS_DASH_LABEL,
  MASTERY_REPORT_LABEL,
  LEARNING_CHECKPOINT_LABEL,
  PERFORMANCE_TASK_LABEL,
  PROFESSIONAL_LEARNING_LABEL,
  MASTERY_REPORT_ID,
} from './constants';
import {
  TYPE_FILTERS,
  isFilterVisible,
} from 'chameleon-src/components/assignments/constants';
import {
  isPreApSubject,
  PPC,
  QB,
  REPORTS,
  LABELS,
  MY_ASSIGNMENTS,
  CS_PERFORMANCE,
} from 'chameleon-src/constants/subjects';
import { STATUS } from 'chameleon-src/constants/assessmentStatus';
import SecondLevelNavList from 'chameleon/ui-stack/ui/base/second-level-nav-list';
import SecondLevelItem from 'chameleon/ui-stack/ui/sidebar-two-levels/second-level-item';
import { QUESTION_TYPES } from 'legacy/src/components/questionBank/editor/AuthorItem';
import { findSubjectByMasterSubjectId } from 'legacy-src/components/learnosity/utils/helpers';
import {
  DRAWING_SUBJECTS,
  AP_RESEARCH,
} from 'chameleon/src/constants/subjects';
import {
  isResourceVisibilityOverriddenByFF,
  isSubjectHasResource,
} from 'chameleon-src/utility/subjectResources';
import { clearEmptyUnits, loadingBars } from './utils';
import { setVisitedUnit as setVisitedUnitAction } from './state/actions';
import generateStudentNavItemsList from './generateStudentNavItemsList';

const shouldOpen = (itemId, items) => {
  for (const item of items) {
    if (item.id === itemId && item.open) {
      return item.open;
    }
  }
  return null;
};

const SidebarWrapper = (props) => {
  const {
    router,
    courseOutline: { loading, units } = {},
    userData,
    setVisitedUnit,
    visitedUnits,
    crShowPracticeExams,
    sharedAssessments,
    internalDAB,
    masteryReportFF,
    studentPracticeProblems,
    worldLanguagePlayerFF,
    unitHasAssignedWLResources,
    userSubjects,
    afamEp26FullLaunchP1FF,
  } = props;
  const {
    location: { pathname, query, search: routerQueryString },
  } = router;
  const {
    me: { initId },
    isTeacher,
    isStudent,
    isAdmin,
    isCoordinator,
  } = userData;
  const {
    addParamsToCurrentLocation,
    queryParams,
    isAssignmentsPage,
    isAssignmentProgressPage,
    isHomePage,
    assignmentsPagePath,
    params,
    isDashboardPage,
    isActivityReportPage,
    isQuestionBankPage,
    isAllQuestionsQBPage,
    isBuildQuizQBPage,
    isQBPageWithCreateQuiz,
    isUsageReportPage,
    isSharedAssessmentPage,
    isMasteryReportPage,
    isAuthorNewQuestionPage,
  } = useNavigation({
    router,
  });
  const [firstLevelActive, setFirstLevelActive] = useState('');
  const { status: resourceStatus, unit } = queryParams || {};
  const { subject: masterSubject } = params || {};
  const currentUnitNumber = useLastAccessedUnit({
    visitedUnits,
    subjectId: masterSubject,
    initId,
    unit,
  });

  const { type: subjectType } =
    findSubjectByMasterSubjectId(userSubjects, masterSubject) || {};

  const onUnitNavItemClick = (event, number) => {
    setVisitedUnit({
      userId: initId,
      subjectId: masterSubject,
      unitNumber: number,
    });
  };

  const isProfessional = isTeacher || isAdmin || isCoordinator;
  const isAdminOrCoordinator = isAdmin || isCoordinator;

  const showMasteryReport =
    masteryReportFF &&
    ![...DRAWING_SUBJECTS, AP_RESEARCH].includes(
      Number.parseInt(params.subject, 10),
    ); // FY30765: Do not Show Mastery Report for these subjects

  // Following the new rules of `Assements pages` the `All` tab disappear and the next first default tabs depends
  // on the AP and feature flags rules
  const firstAssignmentSubTypeFilter = useCallback(
    (status) =>
      TYPE_FILTERS(studentPracticeProblems, null, null, subjectType).filter(
        (filter) => isFilterVisible(masterSubject, status, filter),
      )?.[0]?.value,
    [masterSubject, studentPracticeProblems, subjectType],
  );

  const getAssignmentToLink = (status) => {
    if (isAdminOrCoordinator && status !== STATUS.ALL_RESOURCES) {
      return addParamsToCurrentLocation(
        { assignmentExampleModal: status },
        pathname,
      );
    }
    return addParamsToCurrentLocation(
      { status, type: firstAssignmentSubTypeFilter(status), section: null },
      isAssignmentsPage ? null : `/${masterSubject}${assignmentsPagePath}`,
      ['page', 'unit', 'quizId', 'editMode', 'labels', 'searchTerm'],
    );
  };

  const assignmentStatuses = getAssignmentStatuses(isProfessional, isStudent);

  const masterSubjectWithAssignmentsPagePath = `/${masterSubject}${assignmentsPagePath}`;

  const qbChildren = useMemo(
    () => [
      {
        id: 'all-questions',
        label: 'All Questions',
        to: `/${params.subject}/question_bank/questions${
          pathname.includes('question_bank') ? routerQueryString : ''
        }`,
        isActive:
          (isAllQuestionsQBPage || isBuildQuizQBPage) &&
          !isQBPageWithCreateQuiz,
      },
      {
        id: 'create-quiz',
        label: 'Create Quiz',
        to: addParamsToCurrentLocation(
          {
            action: PAGE_ACTIONS.CREATE_QUIZ,
          },
          pathname.includes('question_bank')
            ? ''
            : `/${params.subject}/question_bank/questions`,
          pathname.includes('question_bank') ? [] : ['searchTerm'],
        ),
        isActive: isQBPageWithCreateQuiz,
      },
      {
        id: `authoring-question`,
        label: `Author Question`,
        to: `/${params.subject}/author/question/${QUESTION_TYPES.MCQ}`,
        isActive: isAuthorNewQuestionPage(),
      },
    ],
    [isAllQuestionsQBPage, isQBPageWithCreateQuiz, routerQueryString, pathname],
  );
  let assignmentsItems = assignmentStatuses.map(
    ({ id, label, status, others = [], aliases = [] }) => {
      const isActive =
        resourceStatus === status ||
        (others && !!others.length && !others.includes(resourceStatus)) ||
        resourceStatus
          ?.split(',')
          .every((selectedStatus) =>
            aliases?.find((alias) => alias === selectedStatus),
          );
      return {
        id,
        label,
        isVisible:
          id !== '22'
            ? () => isSubjectHasResource(params.subject, id)
            : () => isSubjectHasResource(params.subject, QB),
        to: getAssignmentToLink(status),
        isActive:
          (isAssignmentsPage && isActive) ||
          get(query, ['assignmentExampleModal']) === status,
      };
    },
  );
  if (sharedAssessments && isSubjectHasResource(params.subject, QB)) {
    assignmentsItems = assignmentsItems.concat([
      {
        id: 25,
        label: 'Shared Quizzes',
        showNotifIcon: false,
        isVisible: sharedAssessments,
        notifIconTitle: false,
        to: `/${masterSubject}/shared-quizzes`,
        isActive: isSharedAssessmentPage,
        indent: false,
      },
    ]);
  }

  const items = [
    {
      id: 'question-bank',
      label: 'Question Bank',
      iconName: 'question-bank',
      isVisible: () => isSubjectHasResource(params.subject, QB),
      to: undefined,
      children: qbChildren,
    },
    {
      id: MY_ASSIGNMENTS,
      label: 'Resources & Assignments',
      iconName: 'resources-assignments',
      notifIconTitle: 'You have students to score',
      children: assignmentsItems,
      extraChildren: isSubjectHasResource(params.subject, LABELS)
        ? SidebarLabelDisplay
        : null,
    },
  ];

  const reportsChildren = () => {
    const children = [
      {
        id: '31',
        label: 'All Assignments',
        to: `/${params.subject}/activities`,
        isActive: isActivityReportPage,
      },
    ];

    if (isPreApSubject(params.subject)) {
      children.push(
        {
          id: '310',
          label: LEARNING_CHECKPOINT_LABEL,
          to: `/${params.subject}/learning_checkpoint`,
          isActive: false,
        },
        {
          id: '311',
          label: PERFORMANCE_TASK_LABEL,
          to: `/${params.subject}/performance_task`,
          isActive: false,
        },
      );
    } else {
      children.push({
        id: '32',
        label: PROGRESS_DASH_LABEL,
        to: `/${params.subject}/dashboard`,
        isActive: isDashboardPage,
        isVisible: () => isSubjectHasResource(params.subject, PPC),
      });
    }

    if (isAdmin || isCoordinator) {
      children.push({
        id: '33',
        label: 'Usage',
        to: `/${params.subject}/usage`,
        isActive: isUsageReportPage,
      });
    }

    children.push({
      id: MASTERY_REPORT_ID,
      label: MASTERY_REPORT_LABEL,
      to: `/${params.subject}/reports/csperformance`,
      isActive: isMasteryReportPage,
      isVisible: () =>
        isSubjectHasResource(params.subject, CS_PERFORMANCE) ||
        isResourceVisibilityOverriddenByFF(
          params.subject,
          CS_PERFORMANCE,
          isStudent,
          {
            mastery_report: masteryReportFF,
          },
        ),
    });

    return children;
  };

  items.push({
    id: 'reports',
    label: REPORTS_LABEL,
    iconName: 'reports',
    children: reportsChildren(),
    isVisible: () =>
      isSubjectHasResource(params.subject, REPORTS) ||
      isResourceVisibilityOverriddenByFF(params.subject, REPORTS, isStudent, {
        afam_ep26_full_launch_p1: afamEp26FullLaunchP1FF,
      }),
  });

  if (isProfessional && isPreApSubject(params.subject)) {
    items.push({
      id: 'professional-learning',
      label: PROFESSIONAL_LEARNING_LABEL,
      iconName: 'professional_learning',
      open: PROFESSIONAL_LEARNING_SSO_URL,
    });
  }

  if (internalDAB) {
    items.push({
      id: 'dynamic-assessments',
      label: 'Dynamic Assessments',
      iconName: 'resources-assignments',
      to: `/${params.subject}/dynamic-assessments`,
    });
  }

  const studentsNavItemsList = generateStudentNavItemsList({
    isAssignmentsPage,
    masterSubjectWithAssignmentsPagePath,
    addParamsToCurrentLocation,
    resourceStatus,
    params,
    isDashboardPage,
    showMasteryReport,
    isMasteryReportPage,
    queryParams,
    isStudent,
    afamEp26FullLaunchP1FF,
  });

  const unitsFeatureFlagsMaps = {
    // TODO should we use the unitNumber?
    'Practice Exams': crShowPracticeExams,
    Pilot:
      (!isStudent && worldLanguagePlayerFF) ||
      (isStudent && unitHasAssignedWLResources),
  };

  const filterUnitControlledByFF = ({ displayName }) => {
    // if the displayName is in the FF maps then use it otherwise
    // else return true if not found in the unitsFeatureFlagsMaps
    if (displayName in unitsFeatureFlagsMaps) {
      return unitsFeatureFlagsMaps[displayName];
    }
    return true;
  };

  const courseMenuItem = {
    id: 'course-resource',
    label: 'Course Guide',
    iconName: 'course-guide',
    children:
      loading && !(units || []).length
        ? // Show loading bars when units are loading
          loadingBars()
        : // Otherwise show the unit list
          clearEmptyUnits(units || [])
            .filter(filterUnitControlledByFF)
            .map(({ number, displayName }) => ({
              id: number,
              label: displayName,
              isActive: isHomePage && number === currentUnitNumber,
              onClick(event) {
                onUnitNavItemClick(event, number);
                router.push({
                  pathname: `/${params.subject}/home`,
                  query: {
                    unit: number,
                  },
                });
                window.scrollTo(0, 0);
              },
            })),
  };

  const itemsNavList = isStudent
    ? [courseMenuItem].concat(studentsNavItemsList)
    : [courseMenuItem].concat(items);

  let activeParentId = items[0].id;

  useEffect(() => {
    if (
      isAssignmentsPage ||
      isAssignmentProgressPage ||
      isSharedAssessmentPage
    ) {
      activeParentId = 'my-assignments';
    } else if (isQuestionBankPage) {
      activeParentId = 'question-bank';
    } else if (
      isDashboardPage ||
      isActivityReportPage ||
      isUsageReportPage ||
      isMasteryReportPage
    ) {
      activeParentId = 'reports';
    } else if (isHomePage) {
      activeParentId = 'course-resource';
    }

    setFirstLevelActive(activeParentId);
  }, [
    isAssignmentsPage,
    isAssignmentProgressPage,
    isSharedAssessmentPage,
    isQuestionBankPage,
    isDashboardPage,
    isActivityReportPage,
    isUsageReportPage,
    isMasteryReportPage,
    isHomePage,
  ]);

  useEffect(() => {
    if (firstLevelActive !== '') {
      const element = document.querySelector(
        'nav ul li:not([class*="first-level-item"]) a',
      );

      element?.focus();
    }
  }, [firstLevelActive]);

  const setFirstLevelActiveOrRedirect = (itemId, items) => {
    const open = shouldOpen(itemId, items);
    if (open) {
      window.open(open, '_blank');
    } else {
      setFirstLevelActive(itemId);
    }
  };

  return (
    <Sidebar>
      <FirstLevelNavList
        activeParentId={firstLevelActive}
        items={itemsNavList}
        childrenContainer={NavItemsContainer}
        itemTag={FirstLevelItem}
        onItemClick={setFirstLevelActiveOrRedirect}
      />
      <SecondLevelNavList
        itemTag={SecondLevelItem}
        items={itemsNavList}
        firstLevelActive={firstLevelActive}
      />
    </Sidebar>
  );
};

SidebarWrapper.propTypes = {
  courseOutline: PropTypes.object,
};

SidebarWrapper.defaultProps = {
  courseOutline: {},
};

const mapStateToProps = ({
  userSubjects,
  userData,
  subjectData,
  visitedUnits,
  events: { learnosity_sessions = {} } = {},
  sessionIdsBySubject = {},
  featureFlag: {
    cr_show_practice_exams: crShowPracticeExams,
    shared_assessments: sharedAssessments,
    // Feature flag for the Dynamic Assessment Page
    internal_dab_ui: internalDAB,
    // Feature flag for the new Trend Reporting Page
    mastery_report: masteryReportFF,
    student_practice_problems,
    spp_filter_by_subject,
    spp_filter_by_school,
    world_language_player: worldLanguagePlayerFF,
    afam_ep26_full_launch_p1: afamEp26FullLaunchP1FF,
  },
}) => ({
  userSubjects,
  userData,
  subjectData,
  visitedUnits,
  learnosity_sessions,
  sessionIdsBySubject,
  crShowPracticeExams,
  sharedAssessments,
  internalDAB,
  masteryReportFF,
  worldLanguagePlayerFF,
  studentPracticeProblems:
    student_practice_problems ||
    (spp_filter_by_subject && spp_filter_by_school),
  afamEp26FullLaunchP1FF,
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  // because params comes from withRouter, which isn't provided in mapStateToProps, I am handling the merging of state
  // and router params here, so the component doesn't need to know about that data. It should only really know if we
  // want to show the indicator or not.

  const {
    params: { subject: masterSubjectId },
  } = ownProps;
  const { sessionIdsBySubject, learnosity_sessions } = stateProps;

  // filter the sessions from redux by subject id and get the relevant session data
  const sessionIds = get(sessionIdsBySubject, masterSubjectId, []);
  const subject_learnosity_sessions = sessionIds.map(
    (session_id) => learnosity_sessions[session_id],
  );

  // reduce it down to a boolean where the session is submitted and scored
  let showReadyToScoreIndicator = reduce(
    subject_learnosity_sessions,
    (acc, session) =>
      acc ||
      (session.teacher_scoring_completed_at == null &&
        session.student_scoring_completed_at == null &&
        session.submitted_at != null),
    false,
  );
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    showReadyToScoreIndicator,
  };
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    { setVisitedUnit: setVisitedUnitAction },
    mergeProps,
  ),
  withCourseOutline(),
  connectLean({
    /**
     * We want to know if any World Language resources are assigned
     * WL is Unit -3, and it is in only one subject atm. Spanish Lang,
     * for the pilot.
     * */
    scope: () => ['units', `unit--3`],
    mapState(state) {
      const { resources } = state;
      return {
        unitHasAssignedWLResources: Object.keys(resources || {}).length > 0,
      };
    },
  }),
)(SidebarWrapper);
